import pdfToText from 'react-pdftotext'
import mammoth from 'mammoth'

/**
 * Validates if a file is of supported type and within size limits
 * @param {File} file - The file to validate
 * @returns {Object} - { valid: boolean, error: string }
 */
export const validateFile = (file) => {
  const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB in bytes
  const SUPPORTED_TYPES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  ]

  if (!file) {
    return { valid: false, error: 'noFileSelected' }
  }

  // Check file type
  if (!SUPPORTED_TYPES.includes(file.type)) {
    return { valid: false, error: 'invalidFileType' }
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: 'fileTooLarge' }
  }

  return { valid: true, error: null }
}

/**
 * Extracts text from a PDF file
 * @param {File} file - The PDF file
 * @returns {Promise<string>} - Extracted text
 */
export const extractTextFromPDF = async (file) => {
  try {
    const text = await pdfToText(file)
    return text
  } catch (error) {
    console.error('Error extracting text from PDF:', error)
    throw new Error('Failed to extract text from PDF file')
  }
}

/**
 * Extracts text from a DOCX file
 * @param {File} file - The DOCX file
 * @returns {Promise<string>} - Extracted text
 */
export const extractTextFromDOCX = async (file) => {
  try {
    const arrayBuffer = await file.arrayBuffer()
    const result = await mammoth.extractRawText({ arrayBuffer })
    return result.value
  } catch (error) {
    console.error('Error extracting text from DOCX:', error)
    throw new Error('Failed to extract text from DOCX file')
  }
}

/**
 * Gets the file extension from a filename
 * @param {string} filename - The filename
 * @returns {string} - File extension (lowercase)
 */
export const getFileExtension = (filename) => {
  return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase()
}

/**
 * Main function to extract text from supported file types
 * @param {File} file - The file to extract text from
 * @returns {Promise<string>} - Extracted text
 */
export const extractTextFromFile = async (file) => {
  const validation = validateFile(file)
  
  if (!validation.valid) {
    throw new Error(validation.error)
  }

  const extension = getFileExtension(file.name)

  switch (extension) {
    case 'pdf':
      return await extractTextFromPDF(file)
    case 'docx':
      return await extractTextFromDOCX(file)
    default:
      throw new Error('invalidFileType')
  }
}
